import { fetchApi } from '../../../../lib/fetchApi';
import { apiService } from '../../../../services/api/apiService';
import { Pagination, PaginationFilterDto } from '../../../../types/Pagination';
import { SelectItemDto } from '../../../../types/SelectItemDto';
import { CopySurveyFormDto, SurveyDetailDto, SurveyListPagination, SurveyFilterDto, SurveyEndRequestDto, SurveySelectorsDataDto, SurveyFormDto, SurveyParticipantListItem } from "../types";
import { downloadCsvFile } from '../../../../utils/downloadCsvFile';
import { DownloadCsvSource } from '../../../../types/DownloadCsvTypes';

const BASE_URL_ADMIN = "/AdminSurveys";
const BASE_URL = "/surveys";

export const getSurvey = async (idSurvey: number): Promise<SurveyDetailDto>=> {
    try{
        const response = await fetchApi.get(`${BASE_URL}/detail/${idSurvey}`) as SurveyDetailDto;
    return response;
    }catch (error) {
        console.error('Error fetching:', error);
        throw error;
    }
};

export const getSurveys = async (body: SurveyFilterDto): Promise<SurveyListPagination> => {
    const normalizedBody = {
        ...body,
        status: body.status === -1 ? null : body.status,
    };

    const data = await fetchApi.post(`${BASE_URL_ADMIN}/search/`, normalizedBody) as SurveyListPagination;

    return data;
};

export const deleteSurvey = async (idSurvey: number): Promise<SurveyDetailDto> => {
    const response = await fetchApi.put(`${BASE_URL_ADMIN}/delete/${idSurvey}`) as SurveyDetailDto;
    return response;
}

export const getSelectorsData = async (): Promise<SurveySelectorsDataDto> => {
    try {
        const response = await fetchApi.get(`${BASE_URL_ADMIN}/selectors-data`) as SurveySelectorsDataDto;
        return response;
    } catch (error) {
        console.error('Error fetching:', error);
        throw error;
    }
}

export const endSurvey = async (id: number, body: SurveyEndRequestDto): Promise<void> => {
    await fetchApi.put(`${BASE_URL_ADMIN}/end/${id}`, body);
};

export const exportToCsv = async(): Promise<void> => {
    try {        
        const response = await fetchApi.post<DownloadCsvSource>(`${BASE_URL_ADMIN}/export`, {}, {
            responseType: 'arraybuffer',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        let arrayBufferData = response;
        if (!arrayBufferData) {
            if (response instanceof ArrayBuffer) {
                arrayBufferData = response;
            }
        }

        if (!arrayBufferData) {
            throw new Error('No ArrayBuffer data found in response');
        }

        const blob = new Blob([arrayBufferData as BlobPart], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const fileBase = 'admin_surveys';
        const dateSuffix = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        link.setAttribute('download', `${fileBase}_${dateSuffix}.csv`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Error downloading CSV:', error);
        alert('Failed to download CSV. Please try again.');
        throw error;
    }
};

export const getSurveyFormById = async (surveyId: number): Promise<SurveyFormDto>=> {
    const response = await fetchApi.get(`${BASE_URL_ADMIN}/${surveyId}`) as SurveyFormDto;
    return response;
};


export const createSurvey = async (survey: SurveyFormDto): Promise<void> => {
    try {
        await fetchApi.post(`${BASE_URL_ADMIN}/create`, survey);
       
    } catch (error) {
        console.error("Error creating new survey:", error);
        throw error;
    }
};

const getAllSurveyParticipants = async (surveyId: number): Promise<SurveyParticipantListItem[]> => {
    const allParticipants: SurveyParticipantListItem[] = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
        const page = await getSurveyParticipants(surveyId, { pageNumber, pageSize: 50, selectedOrganizationalStructureIds: [] });
        allParticipants.push(...(page.items ?? []));
        totalPages = page.totalPages ?? 0;
        pageNumber++;
    } while (pageNumber <= totalPages);

    return allParticipants;
};

export const copySurveyAndCreateNew = async (copySurveyDto: CopySurveyFormDto): Promise<void> => {
    try {
        const originalSurvey = await getSurveyFormById(copySurveyDto.idOriginalSurvey);
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const emptyParticipants: Pagination<SurveyParticipantListItem> = {
            page: 1,
            size: 10,
            totalPages: 0,
            totalRecords: 0,
            items: []
        };

        const copiedQuestions = copySurveyDto.copyQuestions
            ? originalSurvey.questions.map((question, questionIndex) => ({
                ...question,
                id: 0,
                index: questionIndex,
                answerOptions: (question.answerOptions ?? []).map((answer, answerIndex) => ({
                    ...answer,
                    id: 0,
                    index: answerIndex
                }))
            }))
            : [];

        let copiedParticipants: Pagination<SurveyParticipantListItem> = emptyParticipants;
        if (copySurveyDto.copyParticipants) {
            const originalParticipants = await getAllSurveyParticipants(copySurveyDto.idOriginalSurvey);

            copiedParticipants = {
                page: 1,
                size: originalParticipants.length,
                totalPages: 1,
                totalRecords: originalParticipants.length,
                items: originalParticipants
            };
        }

        const newSurvey: SurveyFormDto = {
            id: null,
            surveyName: copySurveyDto.newSurveyName.trim(),
            campaignId: copySurveyDto.idNewSurveyCampaign,
            campaignName: null,
            startDate: copySurveyDto.copyGeneralData ? (originalSurvey.startDate ?? now) : now,
            endDate: copySurveyDto.copyGeneralData ? (originalSurvey.endDate ?? tomorrow) : tomorrow,
            functionalAreaId: copySurveyDto.copyGeneralData ? originalSurvey.functionalAreaId : null,
            status: copySurveyDto.copyGeneralData ? originalSurvey.status : 0,
            sendBySMS: copySurveyDto.copyGeneralData ? originalSurvey.sendBySMS : false,
            sendByEmail: copySurveyDto.copyGeneralData ? originalSurvey.sendByEmail : false,
            questions: copiedQuestions,
            participants: copiedParticipants
        };

        await createSurvey(newSurvey);
    } catch (error) {
        console.error("Error creating new survey:", error);
        throw error;
    }
};

export const updateSurvey = async (surveyId: number, survey: SurveyFormDto): Promise<void> => {
    try {
        await fetchApi.put(`${BASE_URL_ADMIN}/${surveyId}`, survey, {
            params: { surveyId },
        });
    } catch (error) {
        console.error("Error creating new survey:", error);
        throw error;
    }
};

export const getSurveysByCampaignId = async (campaignId: number): Promise<SelectItemDto[]> => {
    return await fetchApi.get(`${BASE_URL}/byCampaign/${campaignId}`) as SelectItemDto[];
};

export const importParticipantsFromCSV = async (formData: FormData): Promise<SurveyParticipantListItem[]> => 
    await apiService.post<FormData, SurveyParticipantListItem[]>(`${BASE_URL_ADMIN}/csv/participants`, formData);


export const getSurveyParticipants = async (surveyId: number, body: PaginationFilterDto): Promise<Pagination<SurveyParticipantListItem>> => {
    try{
        const response = await fetchApi.post(`${BASE_URL_ADMIN}/participants/${surveyId}`, body ) as Pagination<SurveyParticipantListItem>;
        return response;
    }catch(error){
        throw error;
    }
};

export const deleteSurveyParticipants = async (id: number) => {
    await fetchApi.put(`${BASE_URL}/deleteParticipant`, null, {
        params: { surveyCampaignParticipants: id },
    });
}

export const exportSurveyParticipantsCsv = async (surveyId: number): Promise<void> =>{
    try{
        const result = await fetchApi.post<DownloadCsvSource>(`${BASE_URL_ADMIN}/${surveyId}/participants/exportCsv`, {}, {
            responseType: "blob",
            headers: { "Content-Type": "application/json" }
        });

        downloadCsvFile(result,"export_survey_participants.csv"); 
    }catch(error){
        console.error("Error downloading CSV", error);
        alert('Failed to download CSV. Please try again.');
        throw error;
    }
}

export const downloadParticipantsTemplate = async (headers: string[], fileName: string): Promise<void> => {
    const response = await fetchApi.post<Blob>(
        `${BASE_URL_ADMIN}/participants-template`, 
        {        
            headers,
            fileName
        }, { responseType: 'blob' }
    );

    const url = URL.createObjectURL(response);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
}